import React, { useEffect, useRef } from 'react';
import { GiMeat, GiKnifeFork, GiHotMeal } from 'react-icons/gi';
import { FaLeaf, FaFire } from 'react-icons/fa';
import './About.css';

const features = [
  {
    icon: <FaFire />,
    title: 'Flame Grilled Perfection',
    desc: 'Every kebab is cooked over an open flame, locking in bold flavours and a smoky char that keeps our customers coming back night after night.',
  },
  {
    icon: <FaLeaf />,
    title: 'Fresh Every Day',
    desc: 'We prepare our meats, sauces, and doughs fresh daily — from marinated chicken tikka to hand-stretched pizza bases and aromatic curry pastes.',
  },
];

const About = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="about section-padding" ref={sectionRef}>
      {/* Background decoration */}
      <div className="about-bg-deco" />

      <div className="container">
        <div className="about-grid">

          {/* Left: Visual Panel */}
          <div className="about-visual reveal reveal-left">
            <div className="about-visual-card">
              {/* Central icon display */}
              <div className="visual-center">
                <div className="visual-ring outer-ring" />
                <div className="visual-ring inner-ring" />
                <div className="visual-icon-wrap">
                  <GiMeat className="visual-main-icon" />
                </div>
                {/* Floating accent items */}
                <div className="visual-accent accent-1">
                  <GiHotMeal />
                  <span>Flame Grilled</span>
                </div>
                <div className="visual-accent accent-2">
                  <GiKnifeFork />
                  <span>Fast Delivery</span>
                </div>
              </div>

              {/* Experience badge */}
              <div className="about-experience-badge">
                <span className="badge-years">15</span>
                <span className="badge-label">Years of<br/>Excellence</span>
              </div>
            </div>
          </div>

          {/* Right: Text Content */}
          <div className="about-content">
            <div className="reveal reveal-right">
              <span className="section-tag">Our Story</span>
              <div className="gold-line">
                <div className="gold-diamond" />
              </div>
              <h2 className="section-title about-title">
                Athlone's Favourite<br />Kebab House
              </h2>
            </div>

            <p className="about-text reveal reveal-right" style={{ animationDelay: '0.15s' }}>
              Nestled in the heart of Irishtown, Athlone, King Kebab has been serving
              the community for over 15 years. From sizzling doner kebabs to creamy
              chicken tikka masala, we bring bold, authentic flavours to every order.
            </p>

            <p className="about-text reveal reveal-right" style={{ animationDelay: '0.2s' }}>
              Whether you're craving a classic kebab wrap, a hearty burger meal, a
              wood-fired pizza, or a fragrant Indian curry — our kitchen has it all,
              made fresh and delivered fast to your door.
            </p>

            {/* Key stats bar */}
            <div className="about-stats reveal reveal-right" style={{ animationDelay: '0.25s' }}>
              <div className="about-stat">
                <span className="astat-num">15+</span>
                <span className="astat-label">Years Serving Athlone</span>
              </div>
              <div className="about-stat-sep" />
              <div className="about-stat">
                <span className="astat-num">50+</span>
                <span className="astat-label">Menu Items</span>
              </div>
            </div>

            {/* Feature cards */}
            <div className="about-features reveal reveal-right" style={{ animationDelay: '0.3s' }}>
              {features.map((f, i) => (
                <div className="about-feature-card" key={i}>
                  <div className="feature-icon">{f.icon}</div>
                  <div className="feature-body">
                    <h4 className="feature-title">{f.title}</h4>
                    <p className="feature-desc">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
